// src/hooks/useDashboardStats.ts
// Вычисляет статистику дашборда из backend LLM API.
import { useEffect, useState } from 'react';
import {
  getLlmUsageStatsTotal,
  getLlmUsageStats,
  getLlmUsageStatsByScenario,
} from 'services/llmApi';

const FALLBACK_AVG_RESPONSE_SEC = 3.2;

function msToSecondsOneDecimal(ms: number): number {
  return Math.round((ms / 1000) * 10) / 10;
}

export interface ScenarioStatReal {
  scenarioId: string;
  title: string;
  role: string;
  requests: number;
  /** Оценочное количество токенов */
  tokensEstimate: number;
  avgMessages: number;
}

export interface DayActivity {
  day: string;
  date: string; // YYYY-MM-DD для tooltip
  count: number;
}

export interface FeedItemReal {
  id: string;
  scenarioId: string;
  scenarioTitle: string;
  fileName: string;
  time: string;
  status: 'active' | 'completed';
  color: string;
}

export interface DashboardStatsReal {
  // ── РЕАЛЬНЫЕ данные из LLM API ─────────────────────────
  /** Всего запущено сессий */
  totalSessions: number;
  /** Сессий сегодня */
  todaySessions: number;
  /** Всего файлов обработано */
  filesProcessed: number;
  /** Активных дней (уникальные даты) */
  activeDays: number;
  /** Оценка токенов (tokens_in + tokens_out) */
  tokensEstimate: number;
  /** Активность по дням (последние 7 дней) */
  weekActivity: DayActivity[];
  /** Статистика по сценариям */
  scenarioStats: ScenarioStatReal[];
  /** Лента последних сессий */
  feed: FeedItemReal[];

  mockTokenLimit: number;
  /** Среднее время ответа AI, сек (одна цифра после запятой), GET /llm/usage/stats-total; при 0 или ошибке — 3.2 */
  avgResponseTimeSec: number;
  mockMonthTokens: { month: string; used: number }[];

  isLoading: boolean;
}

const RU_MONTHS_SHORT = [
  'Янв',
  'Фев',
  'Мар',
  'Апр',
  'Май',
  'Июн',
  'Июл',
  'Авг',
  'Сен',
  'Окт',
  'Ноя',
  'Дек',
];

// Базовые «исторические» объёмы для трёх предыдущих месяцев (растущий тренд).
// Текущий месяц заполняется реальной оценкой токенов из API.
const PREV_MONTHS_BASE_USED = [320000, 490000, 710000];

/**
 * Последние 4 месяца относительно текущей даты, включая текущий.
 * Текущий месяц идёт последним с used=0 — заполняется реальной оценкой в effect.
 */
function buildMonthTokensSkeleton(now: Date = new Date()): { month: string; used: number }[] {
  const result: { month: string; used: number }[] = [];
  for (let i = 3; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    result.push({
      month: RU_MONTHS_SHORT[d.getMonth()],
      used: i === 0 ? 0 : (PREV_MONTHS_BASE_USED[3 - i] ?? 0),
    });
  }
  return result;
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getWeekMonday(): Date {
  const today = new Date();
  const dow = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const daysFromMonday = dow === 0 ? 6 : dow - 1;
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysFromMonday);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function buildWeekActivityFromApi(
  statsDaily: { date: string; action_count: number }[]
): DayActivity[] {
  const monday = getWeekMonday();
  const days: DayActivity[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const dateStr = toIsoDate(d);
    const found = statsDaily.find((item) => item.date === dateStr);
    days.push({
      day: d.toLocaleDateString('ru', { weekday: 'short' }),
      date: dateStr,
      count: found?.action_count ?? 0,
    });
  }
  return days;
}

export function useDashboardStats(filterUserId?: string): DashboardStatsReal {
  const [stats, setStats] = useState<DashboardStatsReal>(() => {
    const emptyWeek = buildWeekActivityFromApi([]);
    return {
      totalSessions: 0,
      todaySessions: 0,
      filesProcessed: 0,
      activeDays: 0,
      tokensEstimate: 0,
      weekActivity: emptyWeek,
      scenarioStats: [],
      feed: [],
      mockTokenLimit: 2_000_000,
      avgResponseTimeSec: FALLBACK_AVG_RESPONSE_SEC,
      mockMonthTokens: buildMonthTokensSkeleton(),
      isLoading: true,
    };
  });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const today = new Date();
      const dateFrom = getWeekMonday();

      const dateFromStr = toIsoDate(dateFrom);
      const dateToStr = toIsoDate(today);
      const todayStr = dateToStr;

      const [statsTotal, statsDaily, statsByScenario] = await Promise.all([
        getLlmUsageStatsTotal().catch(() => undefined),
        getLlmUsageStats({
          user_id: filterUserId,
          date_from: dateFromStr,
          date_to: dateToStr,
        }).catch(() => undefined),
        getLlmUsageStatsByScenario({
          user_id: filterUserId,
        }).catch(() => undefined),
      ]);

      if (cancelled) return;

      const totalSessions = statsTotal?.action_count ?? 0;

      const todaySessions =
        statsDaily?.reduce(
          (sum, item) => (item.date === todayStr ? sum + item.action_count : sum),
          0
        ) ?? 0;

      const tokensEstimate =
        (statsTotal?.tokens_in_total ?? 0) + (statsTotal?.tokens_out_total ?? 0);

      const rawMs = statsTotal?.response_time_avg;
      const hasValidMs = typeof rawMs === 'number' && !Number.isNaN(rawMs) && rawMs > 0;
      const avgResponseTimeSec = hasValidMs
        ? msToSecondsOneDecimal(rawMs)
        : FALLBACK_AVG_RESPONSE_SEC;

      const weekActivity = buildWeekActivityFromApi(statsDaily ?? []);

      const activeDays = (statsDaily ?? []).filter((item) => item.action_count > 0).length;

      const scenarioStats: ScenarioStatReal[] = (statsByScenario ?? []).map((item) => ({
        scenarioId: item.scenario_id,
        title: item.scenario_title,
        role: '',
        requests: item.action_count,
        tokensEstimate: item.tokens_in_total + item.tokens_out_total,
        avgMessages: 0,
      }));

      const skeleton = buildMonthTokensSkeleton();
      const monthTokens = skeleton.map((m, idx) =>
        idx === skeleton.length - 1 ? { ...m, used: tokensEstimate } : m
      );

      setStats({
        totalSessions,
        todaySessions,
        filesProcessed: statsTotal?.documents_processed ?? 0,
        activeDays,
        tokensEstimate,
        weekActivity,
        scenarioStats,
        feed: [],
        mockTokenLimit: 2_000_000,
        avgResponseTimeSec,
        mockMonthTokens: monthTokens,
        isLoading: false,
      });
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [filterUserId]);

  return stats;
}
